/**
 * AuthAuditLogPage
 *
 * Feature: 123-audit-log-viewer
 * Description: Authentication audit log viewer with filters, quick filters, and export
 * Constitution: v1.12.0
 * Tasks: T019 (page), T024 (filters), T028 (quick filters), T032 (export)
 */

import React, { useState, useCallback, useMemo } from 'react';
import { RefreshCw, Download, ShieldCheck } from 'lucide-react';
import { PageLayout, Button, Alert, Modal, LoadingState, ErrorState } from '../../components/ui';
import AuditDataGrid, {
  ResultChip,
  ViewDetailsButton,
  type ColumnDef,
} from './components/AuditDataGrid';
import AuditDetailPanel from './components/AuditDetailPanel';
import AuditFilters from './components/AuditFilters';
import QuickFilters from './components/QuickFilters';
import { useAuthAuditLog } from '../../hooks/useAuditQuery';
import { exportAuthAuditCsv } from '../../services/auditViewerApi';
import type {
  AuthAuditLogEntry,
  AuthAuditLogFilters,
  QuickFilter,
} from '../../types/audit';
import { formatEastern } from '@/utils/dateFormatter';

function getDefaultDateRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

function get24hRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setHours(start.getHours() - 24);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

const AUTH_QUICK_FILTERS: QuickFilter[] = [
  {
    id: 'failed-logins-24h',
    label: 'Failed Logins (24h)',
    filters: {
      ...get24hRange(),
      eventType: 'login' as const,
      result: 'failed' as const,
    },
  },
  {
    id: 'my-activity',
    label: 'My Activity',
    filters: {
      ...getDefaultDateRange(),
      userEmail: '__CURRENT_USER__',
    },
  },
  {
    id: 'all-failed',
    label: 'All Failed Events',
    filters: {
      ...getDefaultDateRange(),
      result: 'failed' as const,
    },
  },
];

export default function AuthAuditLogPage() {
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  // Filter state
  const [appliedFilters, setAppliedFilters] = useState<AuthAuditLogFilters>({
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Detail panel
  const [selectedEntry, setSelectedEntry] = useState<
    (AuthAuditLogEntry & { _type: 'auth' }) | null
  >(null);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [truncationDialog, setTruncationDialog] = useState<{
    open: boolean;
    total: number;
  }>({ open: false, total: 0 });

  // Quick filter
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);

  // Query
  const { data, isLoading, isError, refetch } = useAuthAuditLog(appliedFilters, { page, pageSize });

  // Columns
  const columns: ColumnDef<AuthAuditLogEntry>[] = useMemo(
    () => [
      {
        id: 'timestamp',
        label: 'Timestamp',
        width: 180,
        render: (row) => {
          const date = new Date(row.timestamp);
          return <span title={date.toISOString()}>{formatEastern(date)}</span>;
        },
      },
      {
        id: 'eventType',
        label: 'Event Type',
        width: 140,
        render: (row) => row.eventType,
      },
      {
        id: 'userEmail',
        label: 'User',
        width: 220,
        render: (row) => row.userEmail || '—',
      },
      {
        id: 'success',
        label: 'Result',
        width: 100,
        render: (row) => <ResultChip success={row.success} />,
      },
      {
        id: 'ipAddress',
        label: 'IP Address',
        width: 140,
        render: (row) => row.ipAddress || '—',
      },
      {
        id: 'details',
        label: '',
        width: 120,
        render: (row) => (
          <ViewDetailsButton
            onClick={() => setSelectedEntry({ ...row, _type: 'auth' })}
          />
        ),
      },
    ],
    []
  );

  const handleApplyFilters = useCallback(
    (filters: AuthAuditLogFilters) => {
      setAppliedFilters(filters);
      setPage(1);
      setActiveQuickFilter(null);
    },
    []
  );

  const handleQuickFilter = useCallback(
    (filter: QuickFilter) => {
      const newFilters: AuthAuditLogFilters = {
        startDate: defaultRange.startDate,
        endDate: defaultRange.endDate,
        ...(filter.filters as Partial<AuthAuditLogFilters>),
      };
      setAppliedFilters(newFilters);
      setPage(1);
      setActiveQuickFilter(filter.id);
    },
    [defaultRange]
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const result = await exportAuthAuditCsv(appliedFilters);
      if (result.blob.size === 0) {
        return;
      }

      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `auth-audit-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (result.truncated) {
        setTruncationDialog({ open: true, total: result.total });
      }
    } catch {
      // Error handled by apiClient interceptor
    } finally {
      setExporting(false);
    }
  }, [appliedFilters]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  const hasData = (data?.data?.length ?? 0) > 0;
  const emptyMessage = appliedFilters.eventType ||
    appliedFilters.result ||
    appliedFilters.userEmail
    ? 'No authentication events match the selected filters'
    : 'No authentication events recorded yet';

  return (
    <PageLayout
      helpId="auto.auth-audit-log"
      title="Authentication Log"
      description="Sign-in and token events across the platform"
      icon={<ShieldCheck className="h-6 w-6" />}
      actions={
        <>
          <Button
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={handleExport}
            disabled={!hasData || exporting}
          >
            Export to CSV
          </Button>
          <Button
            variant="outline"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        </>
      }
    >
      <QuickFilters
        quickFilters={AUTH_QUICK_FILTERS}
        activeFilterId={activeQuickFilter}
        onApply={handleQuickFilter}
      />

      <AuditFilters
        mode="auth"
        initialFilters={appliedFilters}
        onApply={handleApplyFilters}
      />

      {/* Spec 298 FR-006 (row 51): no log means not read yet (pending or paused) or a failed read, never an
          empty log with a total of 0. */}
      {!data ? (
        isError ? (
          <ErrorState title="Could not read the authentication log" onRetry={() => refetch()} />
        ) : (
          <LoadingState rows={6} />
        )
      ) : (
        <AuditDataGrid
          columns={columns}
          rows={data.data ?? []}
          total={data.total}
          page={page}
          pageSize={pageSize}
          loading={isLoading}
          emptyMessage={emptyMessage}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          getRowKey={(row) => row.id}
        />
      )}

      <AuditDetailPanel
        entry={selectedEntry}
        open={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />

      <Modal
        isOpen={truncationDialog.open}
        onClose={() => setTruncationDialog({ open: false, total: 0 })}
        title="Export Limited"
        size="sm"
        footer={
          <Button
            variant="primary"
            onClick={() => setTruncationDialog({ open: false, total: 0 })}
          >
            OK
          </Button>
        }
      >
        <Alert variant="warning">
          Export limited to 10,000 records. {truncationDialog.total.toLocaleString()} total
          records matched. Apply narrower filters for complete data.
        </Alert>
      </Modal>
    </PageLayout>
  );
}
